import React, { useState, useEffect, useRef } from 'react';
import { colors, radii } from '../../theme/tokens';
import { Button, Icon } from '../ui';
import { useToast } from '../Toast';
import { useConfirm } from '../ConfirmModal';
import { tasksApi } from '../../lib/tasksApi';
import { STATUSES, statusMeta, PRIORITIES, priorityMeta, effectiveFields, FieldInput, Avatar } from './taskHelpers';
import TaskChat from './TaskChat';

// Expanded task view: dept-filtered detail fields, dates, created-by, status
// control (drives time tracking), assignee editor, and per-task chat.
const TaskDetail = ({ task, department, taskType, allTaskTypes = [], users = [], onChanged, onDeleted }) => {
  const showToast = useToast();
  const confirm = useConfirm();

  const toIds = (arr) => (arr || []).map((a) => (a && a._id ? a._id : a)).map(String);

  const [assignees, setAssignees] = useState(toIds(task.assignees));
  const [startDate, setStartDate] = useState(task.startDate ? String(task.startDate).slice(0, 10) : '');
  const [deadline, setDeadline] = useState(task.deadline ? String(task.deadline).slice(0, 10) : '');
  const [fieldValues, setFieldValues] = useState({ ...(task.fieldValues || {}) });
  const [saving, setSaving] = useState(false);
  const [busyStatus, setBusyStatus] = useState(false);
  const [busyPriority, setBusyPriority] = useState(false);
  const [checklist, setChecklist] = useState(task.checklist || []);
  const [newItem, setNewItem] = useState('');
  const [draggingIdx, setDraggingIdx] = useState(null);

  const doneCount = checklist.filter((it) => it.done).length;
  const pct = checklist.length ? Math.round((doneCount / checklist.length) * 100) : 0;

  // Kept in sync every render so the drag handlers below (stable references,
  // see next comment) always read the latest reordered list instead of a
  // stale closure over `checklist`.
  const checklistRef = useRef(checklist);
  checklistRef.current = checklist;
  const persistChecklistRef = useRef();

  // Row DOM nodes, indexed by current on-screen position, so drag can find
  // which row the pointer is currently hovering over.
  const itemRefs = useRef([]);

  // Mutable drag session (which item, how many rows, whether one is active).
  const dragRef = useRef({ active: false, fromIndex: null, total: 0 });

  // handleMove/stopDrag are created ONCE (useRef's initial value) and reused
  // for every drag, so add/removeEventListener always target the exact same
  // function reference. This matters: if a previous drag's pointerup never
  // fires (e.g. the mouse is released outside the browser window/tab loses
  // focus), a fresh per-gesture closure would leak its listeners, and the
  // next drag would run two conflicting handlers at once — looking like
  // reordering "worked once, then broke". Stable refs make re-adding a
  // no-op and let us defensively force-stop any stale session below.
  const handleMove = useRef((moveEvent) => {
    const state = dragRef.current;
    if (!state.active) return;
    const y = moveEvent.clientY;
    const refs = itemRefs.current;
    let target = state.fromIndex;
    for (let i = 0; i < state.total; i++) {
      const node = refs[i];
      if (!node) continue;
      const rect = node.getBoundingClientRect();
      if (y < rect.top + rect.height / 2) { target = i; break; }
      target = i + 1;
    }
    target = Math.max(0, Math.min(target, state.total - 1));
    if (target !== state.fromIndex) {
      // Copy both indices into locals FIRST. React runs the updater below on a
      // later tick, by which point state.fromIndex has already advanced to
      // `target` — reading it inside the updater would splice the item out of
      // and back into the same slot, making every reorder a silent no-op.
      const from = state.fromIndex;
      const to = target;
      setChecklist((prev) => {
        const next = [...prev];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        return next;
      });
      state.fromIndex = to;
      setDraggingIdx(to);
    }
  }).current;

  const stopDrag = useRef(() => {
    const state = dragRef.current;
    if (!state.active) return;
    state.active = false;
    document.removeEventListener('pointermove', handleMove);
    document.removeEventListener('pointerup', stopDrag);
    document.removeEventListener('pointercancel', stopDrag);
    window.removeEventListener('blur', stopDrag);
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
    if (state.captureEl && state.pointerId != null) {
      try { state.captureEl.releasePointerCapture(state.pointerId); } catch (_) { /* already released */ }
    }
    setDraggingIdx(null);
    persistChecklistRef.current(checklistRef.current);
  }).current;

  const startDrag = (idx) => (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();

    // Defensively clear out any prior session that never got a pointerup
    // (see the comment above handleMove) before starting a clean one.
    stopDrag();

    dragRef.current = { active: true, fromIndex: idx, total: checklist.length, captureEl: null, pointerId: null };
    setDraggingIdx(idx);
    // Force the "grabbing" cursor for the whole page while dragging — the
    // handle itself is a tiny 28px target, and without this the OS cursor
    // reverts to normal the instant the pointer drifts off it, even though
    // the drag (tracked via document-level listeners below) is still live.
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'grabbing';

    // Route all pointer events for this gesture to the handle regardless of
    // what's visually under the cursor, so fast/imprecise moves can't drop
    // the drag. Some environments don't support it — fall back silently to
    // the plain document-listener tracking below, which still works.
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
      dragRef.current.captureEl = e.currentTarget;
      dragRef.current.pointerId = e.pointerId;
    } catch (_) { /* not supported here — document listeners still cover it */ }

    document.addEventListener('pointermove', handleMove);
    document.addEventListener('pointerup', stopDrag);
    document.addEventListener('pointercancel', stopDrag);
    window.addEventListener('blur', stopDrag);
  };

  // Belt-and-suspenders: if this row collapses/unmounts mid-drag, drop the
  // listeners directly (skip the persist — there's no guarantee the
  // component, and its callbacks, are still around to handle the response).
  useEffect(() => () => {
    document.removeEventListener('pointermove', handleMove);
    document.removeEventListener('pointerup', stopDrag);
    document.removeEventListener('pointercancel', stopDrag);
    window.removeEventListener('blur', stopDrag);
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Checklist changes persist immediately (optimistic), then re-sync ids from
  // the server response so toggles stay stable.
  const persistChecklist = async (next) => {
    setChecklist(next);
    try {
      const updated = await tasksApi.updateTask(task._id, { checklist: next });
      setChecklist(updated.checklist || []);
      onChanged && onChanged(updated);
    } catch (_) {
      showToast('Failed to update checklist', 'error');
    }
  };
  persistChecklistRef.current = persistChecklist;

  const addChecklistItem = () => {
    const text = newItem.trim();
    if (!text) return;
    setNewItem('');
    // Default the item's owner to the task's first assignee.
    persistChecklist([...checklist, { text, done: false, assignee: assignees[0] || null }]);
  };
  const toggleChecklistItem = (idx) =>
    persistChecklist(checklist.map((it, i) => (i === idx ? { ...it, done: !it.done } : it)));
  const setChecklistAssignee = (idx, assignee) =>
    persistChecklist(checklist.map((it, i) => (i === idx ? { ...it, assignee: assignee || null } : it)));
  const removeChecklistItem = (idx) =>
    persistChecklist(checklist.filter((_, i) => i !== idx));

  const visibleFields = effectiveFields(taskType, department._id, allTaskTypes);

  const changeStatus = async (status) => {
    setBusyStatus(true);
    try {
      const updated = await tasksApi.updateTask(task._id, { status });
      onChanged && onChanged(updated);
    } catch (_) {
      showToast('Failed to update status', 'error');
    } finally {
      setBusyStatus(false);
    }
  };

  // Priority persists immediately (like status). Setting "high" also bumps the
  // task back to In Progress — the server enforces that and returns the updated task.
  const changePriority = async (priority) => {
    setBusyPriority(true);
    try {
      const updated = await tasksApi.updateTask(task._id, { priority });
      onChanged && onChanged(updated);
    } catch (_) {
      showToast('Failed to update priority', 'error');
    } finally {
      setBusyPriority(false);
    }
  };

  const toggleAssignee = (id) => {
    setAssignees((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const setField = (key, value) => setFieldValues((prev) => ({ ...prev, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const updated = await tasksApi.updateTask(task._id, {
        assignees,
        startDate: startDate || null,
        deadline: deadline || null,
        fieldValues
      });
      onChanged && onChanged(updated);
      showToast('Task saved', 'success');
    } catch (_) {
      showToast('Failed to save task', 'error');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    const ok = await confirm('Delete this task permanently?', { danger: true, confirmText: 'Delete' });
    if (!ok) return;
    try {
      await tasksApi.deleteTask(task._id);
      onDeleted && onDeleted(task._id);
    } catch (_) {
      showToast('Failed to delete task', 'error');
    }
  };

  return (
    <div style={styles.wrap}>
      <div style={styles.metaRow}>
        <div style={styles.metaItem}>
          <span style={styles.metaLabel}>Status</span>
          <select
            value={task.status}
            disabled={busyStatus}
            onChange={(e) => changeStatus(e.target.value)}
            style={{ ...styles.statusSelect, color: statusMeta(task.status).color }}
          >
            {STATUSES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
        <div style={styles.metaItem}>
          <span style={styles.metaLabel}>Created by</span>
          <span style={styles.metaValue}>
            <Avatar user={task.createdBy} size={22} /> {task.createdBy?.name || '—'}
          </span>
        </div>
        <div style={styles.metaItem}>
          <span style={styles.metaLabel}>Priority</span>
          <select
            value={task.priority || 'normal'}
            disabled={busyPriority}
            onChange={(e) => changePriority(e.target.value)}
            style={{ ...styles.statusSelect, color: priorityMeta(task.priority).color, borderColor: priorityMeta(task.priority).color }}
            title="High priority moves the task back to In Progress"
          >
            {PRIORITIES.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>
        <div style={styles.metaItem}>
          <span style={styles.metaLabel}>Type</span>
          <span style={styles.metaValue}>{taskType?.name || '—'}</span>
        </div>
        <div style={styles.metaItem}>
          <span style={styles.metaLabel}>Start date</span>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={styles.dateInput} />
        </div>
        <div style={styles.metaItem}>
          <span style={styles.metaLabel}>Deadline</span>
          <input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} style={styles.dateInput} />
        </div>
      </div>

      <div style={styles.field}>
        <label style={styles.label}>Assignees</label>
        <AssigneeSelect users={users} selected={assignees} onToggle={toggleAssignee} />
      </div>

      {visibleFields.length > 0 && (
        <div style={styles.grid}>
          {visibleFields.map((f) => (
            <div key={f.key} style={styles.field}>
              <label style={styles.label}>{f.label}{f.required ? ' *' : ''}</label>
              <FieldInput field={f} value={fieldValues[f.key]} onChange={(v) => setField(f.key, v)} users={users} />
            </div>
          ))}
        </div>
      )}

      <div style={styles.field}>
        <div style={styles.checkHead}>
          <label style={styles.label}>Checklist</label>
          {checklist.length > 0 && <span style={styles.checkCount}>{doneCount}/{checklist.length}</span>}
        </div>
        {checklist.length > 0 && (
          <div style={styles.progressTrack}>
            <div style={{ ...styles.progressFill, width: `${pct}%` }} />
          </div>
        )}
        {checklist.length > 0 && (
          <div style={{ ...styles.checkItems, ...(draggingIdx !== null ? { pointerEvents: 'none' } : null) }}>
            {checklist.map((it, idx) => (
              <div
                key={it._id || idx}
                ref={(el) => { itemRefs.current[idx] = el; }}
                style={{ ...styles.checkItem, ...(draggingIdx === idx ? styles.checkItemDragging : {}) }}
              >
                <span
                  onPointerDown={startDrag(idx)}
                  // Block the browser's native HTML5 drag. If it starts, the
                  // browser stops sending pointermove/mousemove entirely and
                  // switches to drag events, so the reorder gesture dies the
                  // moment you move — which is exactly what it looked like.
                  draggable={false}
                  onDragStart={(e) => e.preventDefault()}
                  style={{ ...styles.checkHandle, cursor: draggingIdx === idx ? 'grabbing' : 'grab' }}
                  title="Hold and drag to reorder"
                >
                  <Icon name="drag_indicator" size={18} color={colors.textFaint} />
                </span>
                <button type="button" onClick={() => toggleChecklistItem(idx)} style={styles.checkBox} aria-pressed={it.done} title={it.done ? 'Mark not done' : 'Mark done'}>
                  <Icon name={it.done ? 'check_box' : 'check_box_outline_blank'} size={20} color={it.done ? colors.parGreenBright : colors.textMuted} />
                </button>
                <span style={{ ...styles.checkText, ...(it.done ? styles.checkTextDone : {}) }}>{it.text}</span>
                <select
                  value={it.assignee ? String(it.assignee) : ''}
                  onChange={(e) => setChecklistAssignee(idx, e.target.value)}
                  style={styles.checkAssignee}
                  title="Responsible person"
                >
                  <option value="">Unassigned</option>
                  {users.map((u) => (
                    <option key={u._id} value={String(u._id)}>{u.name}</option>
                  ))}
                </select>
                <button type="button" onClick={() => removeChecklistItem(idx)} style={styles.checkDel} title="Remove">
                  <Icon name="close" size={16} color={colors.textFaint} />
                </button>
              </div>
            ))}
          </div>
        )}
        <div style={styles.checkAdd}>
          <input
            type="text"
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addChecklistItem(); } }}
            placeholder="Add a checklist item…"
            style={{ ...styles.input, flex: 1 }}
          />
          <Button variant="ghost" size="sm" icon="add" onClick={addChecklistItem} disabled={!newItem.trim()}>Add</Button>
        </div>
      </div>

      <div style={styles.actions}>
        <Button variant="primary" size="sm" icon="save" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
        <Button variant="danger" size="sm" icon="delete" onClick={remove}>Delete</Button>
      </div>

      <TaskChat taskId={task._id} initialMessages={task.messages || []} users={users} />
    </div>
  );
};

// Multi-select assignee dropdown. `users` arrives pre-sorted by department
// activity (most active first), so the top of the list is the likely owner.
// Opens inline (pushes content down) to avoid being clipped by the row's
// overflow:hidden. Selections toggle local state and persist via the Save button.
const AssigneeSelect = ({ users = [], selected = [], onToggle }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const sel = new Set(selected.map(String));
  const chosen = users.filter((u) => sel.has(String(u._id)));
  const needle = q.trim().toLowerCase();
  const filtered = needle ? users.filter((u) => (u.name || '').toLowerCase().includes(needle)) : users;

  if (users.length === 0) return <span style={styles.muted}>No users available to assign</span>;

  return (
    <div style={styles.selectWrap} ref={ref}>
      <button type="button" style={styles.selectTrigger} onClick={() => setOpen((o) => !o)}>
        <span style={styles.selectTriggerInner}>
          {chosen.length === 0 ? (
            <span style={styles.muted}>Select assignees…</span>
          ) : (
            <span style={styles.chosenRow}>
              {chosen.slice(0, 5).map((u) => <Avatar key={u._id} user={u} size={22} />)}
              <span style={styles.chosenText}>
                {chosen.length === 1 ? chosen[0].name : `${chosen.length} assignees`}
              </span>
            </span>
          )}
        </span>
        <Icon name={open ? 'expand_less' : 'expand_more'} size={18} color={colors.textMuted} />
      </button>

      {open && (
        <div style={styles.selectPanel}>
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search people…"
            style={{ ...styles.input, marginBottom: 6 }}
          />
          <div style={styles.selectList}>
            {filtered.map((u) => {
              const on = sel.has(String(u._id));
              return (
                <button
                  key={u._id}
                  type="button"
                  onClick={() => onToggle(String(u._id))}
                  style={{ ...styles.selectOption, ...(on ? styles.selectOptionOn : {}) }}
                >
                  <Avatar user={u} size={22} />
                  <span style={styles.selectOptionName}>{u.name}</span>
                  <Icon
                    name={on ? 'check_box' : 'check_box_outline_blank'}
                    size={18}
                    color={on ? colors.parGreenBright : colors.textMuted}
                  />
                </button>
              );
            })}
            {filtered.length === 0 && <div style={styles.muted}>No people found.</div>}
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  wrap: { padding: '14px 16px 4px', borderTop: `1px solid ${colors.borderSoft}` },
  metaRow: { display: 'flex', flexWrap: 'wrap', gap: 24, marginBottom: 16 },
  metaItem: { display: 'flex', flexDirection: 'column', gap: 4 },
  metaLabel: { fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: colors.textFaint, fontWeight: 700 },
  metaValue: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 14, color: colors.text, fontWeight: 600 },
  statusSelect: {
    padding: '6px 10px',
    borderRadius: radii.sm,
    border: `1px solid ${colors.border}`,
    background: colors.inputBg,
    fontWeight: 700,
    fontSize: 13,
    cursor: 'pointer'
  },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 14 },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 12, fontWeight: 700, color: colors.textMuted },
  input: {
    padding: '10px 12px',
    borderRadius: radii.sm,
    border: `1px solid ${colors.border}`,
    background: colors.inputBg,
    color: colors.text,
    fontSize: 14,
    boxSizing: 'border-box',
    fontFamily: 'inherit'
  },
  dateInput: {
    padding: '6px 10px',
    borderRadius: radii.sm,
    border: `1px solid ${colors.border}`,
    background: colors.inputBg,
    color: colors.text,
    fontSize: 13,
    fontWeight: 600,
    boxSizing: 'border-box',
    fontFamily: 'inherit'
  },
  selectWrap: { position: 'relative', maxWidth: 420 },
  selectTrigger: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
    width: '100%', padding: '8px 10px', borderRadius: radii.sm,
    border: `1px solid ${colors.border}`, background: colors.inputBg, color: colors.text,
    cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left'
  },
  selectTriggerInner: { display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 },
  chosenRow: { display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 },
  chosenText: { fontSize: 13.5, fontWeight: 600, color: colors.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  selectPanel: {
    marginTop: 6, padding: 8, borderRadius: radii.sm,
    border: `1px solid ${colors.border}`, background: colors.surface,
    boxShadow: '0 8px 24px rgba(0,0,0,0.28)'
  },
  selectList: { display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 240, overflowY: 'auto' },
  selectOption: {
    display: 'flex', alignItems: 'center', gap: 8, width: '100%',
    padding: '7px 8px', borderRadius: radii.sm, border: '1px solid transparent',
    background: 'none', color: colors.text, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit'
  },
  selectOptionOn: { background: 'rgba(47,182,92,0.16)', border: '1px solid rgba(47,182,92,0.5)' },
  selectOptionName: { flex: 1, fontSize: 13.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  muted: { color: colors.textFaint, fontSize: 13 },
  actions: { display: 'flex', gap: 10, marginTop: 4 },
  checkHead: { display: 'flex', alignItems: 'center', gap: 8 },
  checkCount: { fontSize: 12, fontWeight: 800, color: colors.parGreenBright },
  progressTrack: { height: 6, borderRadius: 999, background: colors.pill, overflow: 'hidden', marginTop: 2 },
  progressFill: { height: '100%', background: colors.parGreen, borderRadius: 999, transition: 'width 0.25s ease' },
  checkItems: { display: 'flex', flexDirection: 'column', gap: 2, marginTop: 4 },
  checkItem: { display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', borderRadius: radii.sm },
  checkItemDragging: { background: colors.surfaceSoft, boxShadow: '0 4px 12px rgba(0,0,0,0.25)' },
  checkHandle: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    width: 28, height: 28, marginLeft: -4, marginRight: -4,
    cursor: 'grab', touchAction: 'none',
    WebkitUserSelect: 'none', userSelect: 'none',
    WebkitTouchCallout: 'none', WebkitTapHighlightColor: 'transparent'
  },
  checkBox: { background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'inline-flex', flexShrink: 0 },
  checkText: { flex: 1, fontSize: 14, color: colors.text, wordBreak: 'break-word' },
  checkTextDone: { textDecoration: 'line-through', color: colors.textFaint },
  checkAssignee: { fontSize: 12, padding: '4px 6px', borderRadius: radii.sm, border: `1px solid ${colors.border}`, background: colors.inputBg, color: colors.text, maxWidth: 130, flexShrink: 0, cursor: 'pointer' },
  checkDel: { background: 'none', border: 'none', padding: 4, cursor: 'pointer', display: 'inline-flex', flexShrink: 0, borderRadius: radii.sm },
  checkAdd: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }
};

export default TaskDetail;
